import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {Box, ClickAwayListener, IconButton, Typography} from "@mui/material";
import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {ServiceCategory} from "crqlar-react-common/dist/models/serviceCategory/serviceCategory.model";
import {formatFullDMY} from "crqlar-react-common/dist/utils/date.util";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {formatDuration} from "../../../../digitalSigning/public/format.util";
import {InsightsTooltip, TooltipRow, TooltipTitle} from "../../../../insights/kit/InsightsTooltip";
import {FolioFormat} from "./folioFormat";
import {LineAmounts} from "./folioLine.util";
import {LineRateState} from "./lineRate";

interface Props {
    line: FolioItem
    service?: Service
    categories: ServiceCategory[]
    rate: LineRateState
    amounts: LineAmounts
    format: FolioFormat
    handSet: boolean
    missingVat: boolean
}

/**
 * What a line is charged from: the service's category and duration, the rate used on the line's
 * date and its price, VAT and net, the market code, and what is off. Hover on a desktop, tap on a
 * tablet (a tap elsewhere closes it).
 */
const FolioServiceInfo = ({line, service, categories, rate, amounts, format, handSet, missingVat}: Props) => {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);

    const categoryNames = (service?.categoryIds ?? [])
        .map(id => categories.find(category => category.id === id)?.name)
        .filter(Boolean)
        .join(', ');
    const vat = format.vat(line.taxRateSnap);
    const noRate = !!service && !rate.loading && rate.ratePriceCents == null;
    const warnings = [
        noRate && line.timeSpanStart ? t('No rate on {{date}}', {date: formatFullDMY(line.timeSpanStart)}) : undefined,
        handSet ? t('Price set by hand') : undefined,
        missingVat ? t('No VAT rate set') : undefined,
    ].filter((warning): warning is string => !!warning);

    const card = (
        <Box>
            <TooltipTitle title={service?.name || line.description || t('Service')} subtitle={categoryNames || undefined}/>
            {service?.durationMinutes != null && (
                <TooltipRow label={t('Duration')} value={formatDuration(service.durationMinutes, format.language)}/>
            )}
            <TooltipRow label={t('Rate')} value={rate.loading ? '…' : rate.calculatedRate?.name || '—'}/>
            <TooltipRow label={t('Rate price')} value={rate.ratePriceCents != null ? format.money(rate.ratePriceCents) : '—'}/>
            <TooltipRow label={t('VAT')} value={vat ? `${format.money(amounts.vatCents)} · ${vat}` : '—'}/>
            <TooltipRow label={t('Net')} value={format.money(amounts.netCents)}/>
            <TooltipRow label={t('Market code')} value={line.serviceInventoryCode || '—'}/>
            {warnings.map(warning => (
                <Typography key={warning} sx={{fontSize: 13, lineHeight: 1.4, color: 'warning.dark', mt: 0.75}}>
                    {warning}
                </Typography>
            ))}
        </Box>
    );

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box component={'span'} sx={{display: 'inline-flex', flexShrink: 0}}>
                <InsightsTooltip
                    title={card}
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    disableTouchListener
                    describeChild
                    placement={'bottom-start'}
                >
                    <IconButton
                        size={'small'}
                        aria-label={t('Service details')}
                        onClick={() => setOpen(true)}
                        sx={{p: 0.25}}
                    >
                        <InfoOutlinedIcon sx={{fontSize: 16}} color={warnings.length ? 'warning' : 'action'}/>
                    </IconButton>
                </InsightsTooltip>
            </Box>
        </ClickAwayListener>
    );
};

export default FolioServiceInfo;
